import { useMemo } from "react";
import { FieldHelpContent } from "@/components/FieldHelpDialog";
import {
  ReferenceGuide,
  ReferenceGuideLookupParams,
  getReferenceGuide,
  splitContentIntoSteps,
} from "@/shared/referenceId";

export interface ReferenceIdHelpOptions extends ReferenceGuideLookupParams {
  /**
   * Override the default help link label when no specific client match exists.
   */
  fallbackLinkLabel?: string;
}

export interface ReferenceIdHelpResult {
  helpLinkText: string;
  helpDialog: FieldHelpContent;
  guide: ReferenceGuide;
  /** Dynamic checkbox label for "I don't know my {label}" */
  noValueCheckboxLabel: string;
  /** The label for the reference field (e.g., "Account Number", "Mortgage Number") */
  label: string;
  /** The subtitle for the reference field (e.g., "Where to find my account number") */
  subtitle: string;
}

const DEFAULT_LINK_LABEL = "How to find my Reference ID";

const toFieldHelpContent = (guide: ReferenceGuide): FieldHelpContent => {
  const steps = splitContentIntoSteps(guide.content).map((text) => ({
    text,
  }));

  const content: FieldHelpContent = {
    title: guide.heading,
    instructionsTitle: guide.heading,
    steps: steps.length
      ? steps
      : [
          {
            text: guide.content.trim(),
          },
        ],
    showStepNumbers: false,
  };

  if (guide.showImage !== false && typeof guide.image === "string") {
    content.image = guide.image;
  }

  return content;
};

const normalizeDependency = (value?: string | null): string =>
  value?.trim().toLowerCase() ?? "";

export const useReferenceIdHelp = ({
  companyName,
  backendName,
  fallbackLinkLabel,
}: ReferenceIdHelpOptions = {}): ReferenceIdHelpResult => {
  return useMemo(() => {
    const guide = getReferenceGuide({ companyName, backendName });

    return {
      guide,
      helpLinkText: guide.linkLabel || fallbackLinkLabel || DEFAULT_LINK_LABEL,
      helpDialog: toFieldHelpContent(guide),
      noValueCheckboxLabel: guide.noValueCheckboxLabel,
      label: guide.label,
      subtitle: guide.subtitle,
    };
  }, [
    normalizeDependency(companyName),
    normalizeDependency(backendName),
    fallbackLinkLabel,
  ]);
};
